import { useMemo } from 'react';
import { CIRCULAR_TXN_KEY, NET_KEY, TOTAL_COLUMN_KEY, circularRevenuePct, formatINR, isOverlappingRow, isSummaryRow, parseINR } from './components/overlap.constants';
import { SORT_DIRECTIONS, SortConfig } from '../../../../../utils/constants/commonConstants';

export interface RevenueTableData {
    fetchSuccess: boolean;
    headers: { name: string; key: string }[];
    rows: any[];
    totalRecords: number;
    records: number;
    // Per-month intra-company share (%), keyed identically to the month column keys.
    intraCompanyMonthly?: Record<string, number>;
    // Per-month intra-company revenue in ₹ (same keys) — used to compute Net B2B Revenue (Total − Intra).
    intraCompanyMonthlyAmount?: Record<string, number>;
    // Org-wide Σ circular ₹ across ALL customers (backend-computed) — the rows above only carry
    // the loaded page, so a client-side sum understates it for orgs beyond one page.
    circularTotalAmount?: number;
}

export interface RevenueTableView {
    displayHeaders: { name: string; key: string }[];
    displayRows: any[];
}

/**
 * Pure derivation of the B2B revenue table's display headers + rows from the raw response.
 * Extracted from the component and exported so the money math (Net B2B Revenue = Total − Intra,
 * the injected Circular/Net columns, and the Total-column sort) is unit-testable without React.
 */
export function buildRevenueTableView(
    tableData: RevenueTableData | undefined,
    isRevenue: boolean,
    sortConfig?: SortConfig,
): RevenueTableView {
    // B2B revenue table gets two extra pinned-right columns — Circular Txn. (% badge) and Net
    // (= Total − Circular) — injected client-side. Vendor Expense keeps the original column set.
    const displayHeaders =
        isRevenue && tableData?.headers?.length
            ? [...tableData.headers, { name: 'Circular Txn.', key: CIRCULAR_TXN_KEY }, { name: 'Net', key: NET_KEY }]
            : tableData?.headers || [];

    const displayRows = (() => {
        let rows = tableData?.rows || [];
        if (!isRevenue || !rows.length) return rows;

        // Relabel the backend "Total Revenue" summary row to "Total B2B Revenue" and append a
        // derived "Net B2B Revenue" row: Net = Total − Intra Company Txn., per month and total
        // (per product — "Net B2B revenue after subtracting Intra Company Txn.").
        const totalRow = rows.find((r) => r?.name === 'Total Revenue');
        rows = rows.map((r) => (r?.name === 'Total Revenue' ? { ...r, name: 'Total B2B Revenue' } : r));

        const round1 = (n: number) => Math.round(n * 10) / 10;
        const totalNum = parseINR(totalRow?.[TOTAL_COLUMN_KEY]);
        // Aggregate circular ₹ — feeds the summary rows' Circular/Net cells. The backend value is
        // org-wide and authoritative whenever present — including an explicit 0 (rows only carry
        // the loaded page); summing the page is a fallback for responses predating
        // `circularTotalAmount`.
        const totalCircular =
            tableData?.circularTotalAmount != null
                ? Number(tableData.circularTotalAmount) || 0
                : (tableData?.rows || []).reduce(
                      (sum, r) => sum + (!isSummaryRow(r) && isOverlappingRow(r) ? parseINR(r.circularValue) : 0),
                      0,
                  );
        const intraAmt: Record<string, number> = tableData?.intraCompanyMonthlyAmount || {};
        const totalIntra = Object.values(intraAmt).reduce((sum, v) => sum + (Number(v) || 0), 0);

        // Intra Company Txn. row — per-month intra share % (backend `intraCompanyMonthly`). Carries
        // the Total B2B row (per-month ₹) and the intra ₹ amounts so each badge can render a rich
        // Total / Intra / Net tooltip, mirroring Circular Txn. The summary columns follow the
        // GST-Enhancement-Phase-2 sheet: Total = Σintra/Total, Net = Σintra/(Total − Σcircular).
        const intra = tableData?.intraCompanyMonthly;
        const intraRow: any =
            intra && Object.keys(intra).length
                ? {
                      name: 'Intra Company Txn.',
                      __isIntra: true,
                      __totalRow: totalRow,
                      __amounts: { ...intraAmt, [TOTAL_COLUMN_KEY]: totalIntra },
                      ...intra,
                  }
                : null;
        if (intraRow && totalNum > 0 && totalIntra > 0) {
            intraRow[TOTAL_COLUMN_KEY] = round1((totalIntra / totalNum) * 100);
            if (totalNum - totalCircular > 0) {
                intraRow[NET_KEY] = round1((totalIntra / (totalNum - totalCircular)) * 100);
                // Base for the Net-column badge's tooltip: the circular-netted total.
                intraRow.__netBase = formatINR(totalNum - totalCircular);
            }
        }

        if (totalRow) {
            // Total B2B Revenue row — aggregate Circular % (Σcircular/Total) and Net ₹ (Total − Σcircular).
            const totalB2bRow: any = rows.find((r) => r?.name === 'Total B2B Revenue');
            if (totalB2bRow) {
                if (totalNum > 0 && totalCircular > 0) {
                    totalB2bRow[CIRCULAR_TXN_KEY] = round1((totalCircular / totalNum) * 100);
                }
                totalB2bRow[NET_KEY] = formatINR(totalNum - totalCircular);
            }

            const netB2bRow: any = {
                ...totalRow,
                name: 'Net B2B Revenue',
                [TOTAL_COLUMN_KEY]: formatINR(totalNum - totalIntra),
                isOverlapping: undefined,
                circularValue: undefined,
                circularTotal: undefined,
                circularPct: undefined,
            };
            // Net per month = that month's Total − that month's intra amount. Months with no intra
            // keep the Total value (net = total).
            Object.keys(intraAmt).forEach((m) => {
                if (totalRow[m] !== undefined) {
                    netB2bRow[m] = formatINR(parseINR(totalRow[m]) - (Number(intraAmt[m]) || 0));
                }
            });
            // Per the sheet: Circular = Σcircular/(Total − Σintra), Net = Total − Σintra − Σcircular.
            if (totalCircular > 0 && totalNum - totalIntra > 0) {
                netB2bRow[CIRCULAR_TXN_KEY] = round1((totalCircular / (totalNum - totalIntra)) * 100);
            }
            netB2bRow[NET_KEY] = formatINR(totalNum - totalIntra - totalCircular);
            rows = [...rows, ...(intraRow ? [intraRow] : []), netB2bRow];
        } else if (intraRow) {
            rows = [...rows, intraRow];
        }

        // Sort customer rows by the chosen column (Total, a month, Circular % or Net); summary
        // rows stay pinned at the bottom in order. Cells with no value ('-'/absent) sort as 0.
        if (!sortConfig?.direction) return rows;
        const field = sortConfig.field || TOTAL_COLUMN_KEY;
        const asc = sortConfig.direction === SORT_DIRECTIONS.ASC;
        // Circular/Net are derived columns — mirror what their cells display.
        const sortValue = (r: any): number => {
            if (field === CIRCULAR_TXN_KEY) return isOverlappingRow(r) ? parseFloat(String(circularRevenuePct(r))) || 0 : 0;
            if (field === NET_KEY) return parseINR(r?.totalRevenue) - (isOverlappingRow(r) ? parseINR(r?.circularValue) : 0);
            return parseINR(r?.[field]);
        };
        const customers = rows.filter((r) => !isSummaryRow(r));
        const summaries = rows.filter((r) => isSummaryRow(r));
        const sorted = [...customers].sort((a, b) => {
            const diff = sortValue(a) - sortValue(b);
            return asc ? diff : -diff;
        });
        return [...sorted, ...summaries];
    })();

    return { displayHeaders, displayRows };
}

/** Memoised React wrapper around buildRevenueTableView. */
export default function useRevenueTableRows(
    tableData: RevenueTableData | undefined,
    isRevenue: boolean,
    sortConfig?: SortConfig,
): RevenueTableView {
    return useMemo(() => buildRevenueTableView(tableData, isRevenue, sortConfig), [tableData, isRevenue, sortConfig]);
}
